/**
 * Interpolate {{variable}} and {{nested.path}} placeholders in a prompt string.
 */
export function interpolatePrompt(prompt: string, data: Record<string, unknown>): string {
  return prompt.replace(/\{\{([^}]+)\}\}/g, (match, path: string) => {
    const value = getNestedValue(data, path.trim());
    if (value === undefined || value === null) return match;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  });
}

export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Parse an AI message as JSON, with multiple fallback strategies for
 * markdown-wrapped, partially malformed, or truncated responses.
 */
export function parseJsonResponse(message: string): Record<string, unknown> {
  // Strategy 1: direct parse

  const typeofMessage = typeof message;
  if (typeofMessage === 'object') {
    return message as unknown as Record<string, unknown>;
  }

  try {
    return JSON.parse(message);
  } catch {
    // continue
  }

  // Strategy 2: extract from markdown code block
  const codeBlockMatch = message.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    try {
      return JSON.parse(codeBlockMatch[1].trim());
    } catch {
      // continue
    }
  }

  // Strategy 3: find first JSON object
  const jsonMatch = message.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[0]);
    } catch {
      // continue
    }
  }

  // Strategy 4: fix common JSON issues (trailing commas, single quotes)
  try {
    const cleaned = message
      .replace(/,(\s*[}\]])/g, '$1')
      .replace(/'/g, '"')
      .trim();
    return JSON.parse(cleaned);
  } catch {
    // continue
  }

  // Strategy 5: repair truncated JSON (response cut off due to token limit)
  const repaired = repairTruncatedJson(message);
  if (repaired) {
    try {
      return JSON.parse(repaired);
    } catch {
      // continue
    }
  }

  throw new Error('Unable to parse AI response as JSON');
}

/**
 * Attempt to repair truncated JSON by finding the last complete object
 * boundary and closing the structure properly.
 */
function repairTruncatedJson(content: string): string | null {
  const jsonStart = content.indexOf('{');
  if (jsonStart === -1) return null;

  const json = content.substring(jsonStart);

  // Find last safe cut points (end of complete objects)
  const fieldEndPattern = /\}[\s]*,?[\s]*(?=\{|$)/g;
  let lastSafeCut = -1;
  let match;

  while ((match = fieldEndPattern.exec(json)) !== null) {
    lastSafeCut = match.index + 1;
  }

  if (lastSafeCut === -1) {
    lastSafeCut = json.lastIndexOf('}');
    if (lastSafeCut === -1) return null;
    lastSafeCut += 1;
  }

  // Try progressively shorter cuts until we find one that can be closed
  for (
    let cutPoint = json.length;
    cutPoint >= lastSafeCut;
    cutPoint = json.lastIndexOf('}', cutPoint - 1) + 1
  ) {
    if (cutPoint <= 0) break;
    const attempt = tryCloseJson(json.substring(0, cutPoint));
    if (attempt) return attempt;
  }

  return tryCloseJson(json.substring(0, lastSafeCut));
}

/**
 * Given a potentially incomplete JSON string, try to close it by:
 * 1. Closing any open string literal
 * 2. Removing trailing incomplete key-value pairs
 * 3. Closing unclosed brackets and braces
 */
function tryCloseJson(partial: string): string | null {
  let s = partial;

  // Determine if we're inside a string by counting unescaped quotes
  let inString = false;
  let escaped = false;

  for (let i = 0; i < s.length; i++) {
    const char = s[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
    }
  }

  // If we ended inside a string, close it
  if (inString) {
    s += '"';
  }

  // Remove trailing partial key:value or comma
  s = s.replace(/,\s*"[^"]*"\s*:\s*"?[^"]*$/, '');
  s = s.replace(/,\s*"[^"]*"\s*$/, '');
  s = s.replace(/,\s*$/, '');

  // Recount brackets/braces (outside strings) and close them
  inString = false;
  escaped = false;
  let openBraces = 0;
  let openBrackets = 0;

  for (let i = 0; i < s.length; i++) {
    const char = s[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (char === '{') openBraces++;
    if (char === '}') openBraces--;
    if (char === '[') openBrackets++;
    if (char === ']') openBrackets--;
  }

  if (openBraces < 0 || openBrackets < 0) return null;

  for (let i = 0; i < openBrackets; i++) s += ']';
  for (let i = 0; i < openBraces; i++) s += '}';

  if (!s.startsWith('{') || !s.endsWith('}')) return null;

  try {
    JSON.parse(s);
    return s;
  } catch {
    return null;
  }
}
